import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExportConfig, WorkflowItemStatus, WorkflowStatus } from '@api/projects/interfaces';
import { ProjectGuardService } from '@app/components/cleanui/system/Guard/project.guard';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { participantPages } from '@app/pages/participant/participants.routes';
import { EWorkflowPathKey } from '@app/pages/participant/workflow/workflow-path-key.enum';
import { InterpolationService } from '@app/services/interpolation/interpolation.service';
import { MenuService } from '@app/services/menu';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { ProjectService } from '@app/services/project/project.service';
import { ReportService } from '@app/services/reports/report.service';
import { WorkflowRestService } from '@app/services/rest/workflow-rest.service';
import { TWorkflowViewMode } from '@app/workflow/components/navigator/interfaces';
import { SpecialQuestionService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { ReportConfigWithVisibility, ReportFilterService } from '@app/workflow/components/report-card/report-filter.service';
import { ResolveReportDataService } from '@app/workflow/components/report-card/resolve-report-data.service';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { WorkflowService } from '@app/workflow/services/workflow.service';
import { IParticipant, IQuestionnaireWorkflowItem, IWorkflow, IWorkflowItem } from '@interfaces/interfaces';
import { ReportConfig } from '@interfaces/reports';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';

@Component({
	selector: 'workflow-details',
	templateUrl: './workflow-details.component.html',
	styleUrls: ['./workflow-details.component.scss'],
	providers: [ProjectGuardService],
})
export class WorkflowDetailsComponent implements OnDestroy {

	private subscription = new Subscription();
	
	workflowItem: IWorkflowItem;
	itemId: string;
	participant: IParticipant;
	workflowItemStatus: WorkflowItemStatus;
	selectedViewMode: TWorkflowViewMode;
	selectedViewMode$ = new BehaviorSubject<TWorkflowViewMode>('wizard');
	recommendedViewMode: TWorkflowViewMode;
	questionnaireWorkflowItem: IQuestionnaireWorkflowItem;
	showStartContinueCard: boolean;

	public workflowStartedContinueSplitButton: SplitButtonElement[];
	public workflowFinishedSplitButton: SplitButtonElement[];
	public backToParticipantSplitButton: SplitButtonElement = {
		title: 'actions.show_participant_detail',
		type: 'default',
		icon: 'X',
		callback: () => this.showParticipant()
	};
	public distractionFreeWizardIcon = 'trello';
	public compactViewIcon = 'book-open';

	public selectedParticipant: Set<string> = new Set<string>();
	public exportConfigs: (ExportConfig & { active?: boolean })[];

	public reportConfigs: ReportConfigWithVisibility[];
	public menuReportConfigs: ReportConfigWithVisibility[];
	public reportData: SessionItemData;

	public printReportConfigs?: ReportConfigWithVisibility[];

	constructor(
		private workflowItemService: WorkflowItemNavigationService,
		private activeParticipantService: ActiveParticipantService,
		private messplatzService: MessplatzService,
		private route: ActivatedRoute,
		private router: Router,
		private workflowRestService: WorkflowRestService,
		private workflowService: WorkflowService,
		private specialQuestionService: SpecialQuestionService,
		private reportService: ReportService,
		private reportFilterService: ReportFilterService,
		private projectService: ProjectService,
		private resolveReportDataService: ResolveReportDataService,
		private interpolationService: InterpolationService,
		private menuService: MenuService,
	) {
		const workflowItem$ = combineLatest([this.workflowService.workflow$, this.paramItemId$]).pipe(
			map(([workflow, itemId]) => this.findWorkflowItem(workflow, itemId)),
		);
		
		const questionnaireWorkflowItem$ = workflowItem$.pipe(
			map(workflowItem => workflowItem?.type === 'Questionnaire' ? workflowItem as IQuestionnaireWorkflowItem : null),
		);
		
		const recommendedViewMode$ = combineLatest([questionnaireWorkflowItem$, this.messplatzService.hardware$]).pipe(
			map(([questionnaireWorkflowItem, ]) => this.getRecommendedViewMode(questionnaireWorkflowItem, false)),
		);

		const reports$ = combineLatest([
			this.workflowService.workflowStatus$,
			this.activeParticipantService.participant$,
			this.reportService.reportConfigs$,
			this.projectService.exportsConfig$,
			this.paramItemId$,
		]).pipe(
			switchMap(async ([workflowStatus, participant, reportConfigs, exportsConfig, itemId]) => {
				const filteredExportsConfig: (ExportConfig & { active?: boolean; })[] = exportsConfig
					.filter(c => c.itemId == itemId && c.showInWorkflowItemDetail)
					.map(c => ({
						...c,
						active: !!(c?.itemId && workflowStatus?.status[c.itemId] && workflowStatus?.status[c.itemId].completed),
					}));
				return {
					...await this.prepareReportConfigs(itemId, participant, workflowStatus, reportConfigs),
					exportsConfig: filteredExportsConfig,
				};
			}),
		);
		
		const workflowItemStatus$ = combineLatest([
			this.workflowService.workflowStatus$,
			this.paramItemId$
		]).pipe(
			map(([status, itemId]) => status?.status[itemId]),
		);
		
		this.subscription.add(workflowItem$.subscribe(workflowItem => this.workflowItem = workflowItem));
		this.subscription.add(questionnaireWorkflowItem$.subscribe(questionnaireWorkflowItem => this.questionnaireWorkflowItem = questionnaireWorkflowItem));
		this.subscription.add(workflowItemStatus$.subscribe(workflowItemStatus => this.workflowItemStatus = workflowItemStatus));
		
		this.subscription.add(reports$.subscribe(reports => {
			this.menuReportConfigs = reports.menuReportConfigs;
			this.reportConfigs = reports.reportConfigs;
			this.reportData = reports.reportData;
			this.exportConfigs = reports.exportsConfig;
		}));
		
		this.subscription.add(recommendedViewMode$.subscribe(recommendedViewMode => this.recommendedViewMode = recommendedViewMode));
		this.subscription.add(recommendedViewMode$.pipe(first()).subscribe(recommendedViewMode => {
			this.selectedViewMode = recommendedViewMode;
			this.selectedViewMode$.next(recommendedViewMode);
		}));
		
		this.subscription.add(combineLatest([
			this.selectedViewMode$,
			workflowItemStatus$,
			questionnaireWorkflowItem$,
		]).subscribe(([selectedViewMode, workflowItemStatus, questionnaireWorkflowItem]) => {
			this.initSplitButtons(selectedViewMode, workflowItemStatus, questionnaireWorkflowItem);
		}));
		
		this.subscription.add(this.activeParticipantService.participant$.subscribe(participant => {
			this.participant = participant;
			this.selectedParticipant = new Set<string>();
			this.selectedParticipant.add(this.participant.id);
		}));
		
		this.subscription.add(this.paramItemId$.subscribe(itemId => {
			this.itemId = itemId;
		}));
				
		this.subscription.add(combineLatest([
			questionnaireWorkflowItem$,
			workflowItemStatus$,
			this.projectService.hasWorkflowWriteOnActiveProject$,
		]).subscribe(([questionnaireWorkflowItem, workflowItemStatus, hasWorkflowWritePermission]) => {
			this.showStartContinueCard = this.isShowStartContinueCard(questionnaireWorkflowItem, workflowItemStatus, hasWorkflowWritePermission);
		}));
		
		this.subscription.add(combineLatest([
			this.projectService.participantsConfig$,
			this.activeParticipantService.participant$,
			workflowItem$,
		]).subscribe(([config, participant, workflowItem]) => {
			const template = config?.details?.info?.name;
			const participantInfoTemplate = template && this.interpolationService.interpolation(template);
			const participantInfo = participantInfoTemplate?.interpolate(participant.data) || participant.id;
			this.menuService.title = `${participantInfo} - ${workflowItem.name}`;
		}));
	}
	
	private findWorkflowItem(workflow: IWorkflow, itemId: string): IWorkflowItem {
		const workflowItem = workflow.items?.find(item => item.id === itemId);
		if (!workflowItem) {
			console.warn(`Missing workflowitem ${itemId}`);
			// TODO: Was tun?
			return null;
		}
		return workflowItem;
	}
	
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public get workflowStartedString(): string {
		return this.workflowItemStatus?.started ? 'status.started' : 'status.unstarted';
	}
	public get workflowStartedContinue(): string {
		return this.workflowItemStatus?.started ? 'actions.continue' : 'actions.start';
	}

	private get paramItemId$() : Observable<string> {
		return this.route.paramMap.pipe(
			map(params => params.get('itemId')),
			distinctUntilChanged()
		);
	}

	private isShowStartContinueCard(questionnaireWorkflowItem: IQuestionnaireWorkflowItem, workflowItemStatus: WorkflowItemStatus, hasWorkflowWritePermission: boolean): boolean {
		return !!questionnaireWorkflowItem && !!(workflowItemStatus?.started || hasWorkflowWritePermission);
	}

	private getRecommendedViewMode(questionnaireWorkflowItem: IQuestionnaireWorkflowItem, privacyMode: boolean): TWorkflowViewMode {
		if (questionnaireWorkflowItem?.groups) {
			let recommendWizard = false;
			
			//alle notwendigen Messgeräte
			//recommendWizard ||= this.workflowService.hasAllDevicesbyQuestionnaire(questionnaireWorkflowItem);
			
			//Mindestens irgendein Messgerät
			recommendWizard ||= this.messplatzService.hasDevices();
			
			//Erweiterter Datenschutzmodus
			recommendWizard ||= privacyMode;
			
			//Individuelle Fragetypen
			recommendWizard ||= questionnaireWorkflowItem.groups.some(group => group.items.some(item => this.specialQuestionService.isSpecialQuestion(item.type)));
			
			return recommendWizard ? 'wizard' : 'compact';
		}
	}
	
	async startOrContinueWorkflow(): Promise<void> {
		try {
			const isContinue = !this.workflowItemStatus?.completed && this.workflowItemStatus?.started;
			if (!this.workflowItemStatus?.completed && !this.workflowItemStatus?.started) {
				await this.workflowRestService.updateStatus$(this.projectService.activeProject?.project_id, this.participant?.id, this.itemId, {}).toPromise();
				this.workflowService.changed();
			}
			const viewMode: TWorkflowViewMode = this.selectedViewMode === 'compact' ? 'compact' : 'wizard';
			await this.router.navigate([participantPages.workflowProcess.base({wfItemId: this.itemId, pageUrl: ''})], { queryParams: { view: viewMode, continue: isContinue }});
		} catch (error) {
			console.error(error);
		}
	}

	showOverview(): void{
		this.router.navigate([this.workflowItemService.getFirstAvailableEntryURL(this.workflowItem, EWorkflowPathKey.OVERVIEW, this.selectedViewMode)]).catch(console.error);
	}

	showParticipant(): void{
		this.router.navigate([participantPages.stammDaten.base()]).catch(console.error);
	}

	public initSplitButtons(selectedViewMode: TWorkflowViewMode, workflowItemStatus: WorkflowItemStatus, questionnaireWorkflowItem: IQuestionnaireWorkflowItem): void {
		//Splitbuttons die für beide Karten verfügbar sind
		const showOverviewInCompactButton: SplitButtonElement = {
			title: 'workflow.show_OverviewInCompact',
			type: 'primary',
			icon: this.compactViewIcon,
			callback: () => this.showOverview()
		};
		const showOverviewInWizardButton: SplitButtonElement = {
			title: 'workflow.show_OverviewInWizard',
			type: 'primary',
			icon: this.distractionFreeWizardIcon,
			callback: () => this.showOverview()
		};

		//SplitButtons Erstellen, zuerst für Starten und fortsetzen.
		this.workflowStartedContinueSplitButton = [];
		const presetBtn: SplitButtonElement = {
			title: this.workflowStartedContinue,
			shortTitle: this.workflowStartedContinue,
			type: 'primary',
			icon: '',
			callback: () => this.startOrContinueWorkflow(),
		};

		//Kompakt Modus ausgewählt
		if (selectedViewMode === 'compact') {
			presetBtn.icon = this.compactViewIcon;

			if (!workflowItemStatus?.started) {
				presetBtn.title = 'workflow.startInCompact';
			}
			else if (workflowItemStatus?.started && !workflowItemStatus?.completed) {
				presetBtn.title = 'workflow.continueInCompact';

			}
			this.workflowStartedContinueSplitButton.push(presetBtn);

			if (questionnaireWorkflowItem?.showOverview) {
				this.workflowStartedContinueSplitButton.push(showOverviewInCompactButton);
			}
		}

		//Ablenkungsfreier Wizard Ausgewählt
		if (selectedViewMode === 'wizard') {
			presetBtn.icon = this.distractionFreeWizardIcon;

			if (!workflowItemStatus?.started) {
				presetBtn.title = 'workflow.startInWizard';
			}
			else if (workflowItemStatus?.started && !workflowItemStatus?.completed) {
				presetBtn.title = 'workflow.continueInWizard';
			}
			this.workflowStartedContinueSplitButton.push(presetBtn);

			if (questionnaireWorkflowItem?.showOverview) {
				this.workflowStartedContinueSplitButton.push(showOverviewInWizardButton);
			}
		}

		//Schritt beendet Button
		this.workflowFinishedSplitButton = [];

		//Kompaktmouds ausgewählt
		if (selectedViewMode === 'compact') {
			if (questionnaireWorkflowItem?.showOverview) {
				this.workflowFinishedSplitButton.push({
					title: 'workflow.show_OverviewInCompact',
					shortTitle: 'workflow.show_Overview',
					type: 'primary',
					icon: this.compactViewIcon,
					callback: () => this.showOverview()
				});
			}
			this.workflowFinishedSplitButton.push({
				title: 'workflow.showInCompact',
				shortTitle: 'workflow.show',
				type: 'primary',
				icon: this.compactViewIcon,
				callback: () => this.startOrContinueWorkflow()
			});
		}
		
		//Anlenkungsfreier Wizard ausgewählt
		if (selectedViewMode === 'wizard') {
			if (questionnaireWorkflowItem?.showOverview) {
				this.workflowFinishedSplitButton.push({
					title: 'workflow.show_OverviewInWizard',
					shortTitle: 'workflow.show_Overview',
					type: 'primary',
					icon: this.distractionFreeWizardIcon,
					callback: () => this.showOverview()
				});
			}
			this.workflowFinishedSplitButton.push({
				title: 'workflow.showInWizard',
				shortTitle: 'workflow.show',
				type: 'primary',
				icon: this.distractionFreeWizardIcon,
				callback: () => this.startOrContinueWorkflow()
			});
		}
	}

	private async prepareReportConfigs(itemId: string, participant: IParticipant, workflowStatus: WorkflowStatus, reportConfigs: ReportConfig[]) {
		if (workflowStatus && participant && reportConfigs && itemId) {
			try {
				const dataResolver = this.resolveReportDataService.createDataResolver(this.projectService.activeProject.project_id, participant.id);
				const usedReportConfigs = await this.reportFilterService.resolveReportConfigs(reportConfigs, participant, new Date(), 'details', itemId, workflowStatus, dataResolver);
				const menuReportConfigs = await this.reportFilterService.resolveReportConfigs(reportConfigs, participant, new Date(), 'details', itemId, workflowStatus, dataResolver, true);
				const reportData = dataResolver.data;
				
				return {
					reportConfigs: usedReportConfigs,
					menuReportConfigs,
					reportData,
				};
			} catch (error) {
				console.error(error);
			}
		}
		return {
			reportConfigs: [],
			menuReportConfigs: [],
			reportData: new SessionItemData({}),
		};
	}

	setPrintReportId(reportId: string): void {
		this.printReportConfigs = reportId ? this.reportConfigs?.filter(report => report.reportId === reportId) : undefined;
	}
}
